/**
 * useActiveSquads — hydrates active squads for a session and applies
 * chat_squad_* WS events with full reducer (SP3-MF-01).
 *
 * Per spec §11.8: on every WS (re)connect, replay the squad audit log
 * since the last seen ts to catch events broadcast during disconnect.
 *
 * Pure reducer lives in useActiveSquadsReducer.ts (separated so jest
 * tests can import it without dragging the api/axios chain).
 */
import { useEffect, useReducer, useRef } from 'react';
import { councilWsClient } from '../services/councilWsClient';
import api from '../services/api';
import { Squad, SquadMember, SquadAuditEvent, TERMINAL_SQUAD_STATES } from '../types/squads';
import {
  ActiveSquadsState,
  INITIAL_STATE,
  reducer,
} from './useActiveSquadsReducer';


export function useActiveSquads(sessionId: string) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // Keep a ref so the WS handler reads the latest state without
  // re-subscribing on every dispatch.
  const stateRef = useRef<ActiveSquadsState>(state);
  stateRef.current = state;

  // Hydrate on mount + when session changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // axios `api` instance has baseURL=/api; paths here must NOT
        // include the /api prefix or the URL doubles to /api/api/...
        // (May 18 hotfix — Alan's field report).
        const { data: squads } = await api.get<Squad[]>(
          `/sessions/${sessionId}/squads`,
          { params: { status: 'running' } },
        );
        const memberLookup: Record<string, SquadMember[]> = {};
        for (const s of squads) {
          const { data: members } = await api.get<SquadMember[]>(
            `/sessions/${sessionId}/squads/${s.id}/members`,
          );
          memberLookup[s.id] = members;
        }
        if (!cancelled) {
          dispatch({ type: 'HYDRATE', squads, members: memberLookup });
        }
      } catch (err) {
        // 404 here is benign — the session may not exist yet (Trailhead
        // mounts ConversationInterface before some sessions are real).
        // Log at warn but don't propagate. (Long-term: gate hook on
        // session existence; for now silence noise.)
        const status = (err as any)?.response?.status;
        if (status !== 404) {
          console.warn('[useActiveSquads] hydrate failed:', err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // WS subscription — handles real-time events + audit replay on (re)connect
  useEffect(() => {
    const handler = async (event: any) => {
      if (event.event_type === 'ws_connected') {
        // Per spec §11.8: replay audit log since lastEventTs for each known
        // squad to catch events that fired while disconnected.
        const snapshot = stateRef.current.bySquadId;
        for (const squadId of Object.keys(snapshot)) {
          const lastTs = snapshot[squadId].lastEventTs;
          try {
            const { data: missed } = await api.get<SquadAuditEvent[]>(
              `/sessions/${sessionId}/squads/${squadId}/events`,
              { params: { since: lastTs } },
            );
            dispatch({ type: 'REPLAY_AUDIT', squadId, events: missed });
          } catch (err) {
            console.warn(`[useActiveSquads] audit replay failed for ${squadId}:`, err);
          }
        }
      } else if (typeof event.event_type === 'string'
                 && event.event_type.startsWith('chat_squad_')) {
        // SP4 hotfix (May 18): the reducer drops events for unknown
        // squad_id (entry == undefined → return state unchanged).
        // Mount-time hydrate only catches squads ALREADY running; any
        // squad dispatched AFTER mount lands here via WS first. Fetch
        // squad+members on first-sight so subsequent member_started /
        // member_completed / squad_completed events find the entry.
        const newSquadId = event?.target?.squad_id;
        if (newSquadId && !stateRef.current.bySquadId[newSquadId]) {
          try {
            const { data: squad } = await api.get<Squad>(
              `/sessions/${sessionId}/squads/${newSquadId}`,
            );
            const { data: members } = await api.get<SquadMember[]>(
              `/sessions/${sessionId}/squads/${newSquadId}/members`,
            );
            dispatch({
              type: 'HYDRATE',
              squads: [squad],
              members: { [newSquadId]: members },
            });
          } catch (err) {
            console.warn(
              '[useActiveSquads] dispatched-hydrate failed for',
              newSquadId, err,
            );
          }
        }
        dispatch({ type: 'WS_EVENT', event });
      }
    };
    return councilWsClient.subscribe(sessionId, handler);
  }, [sessionId]);

  const activeSquads = Object.values(state.bySquadId).filter(
    (s) => !TERMINAL_SQUAD_STATES.has(s.squad.status),
  );
  const terminalSquads = Object.values(state.bySquadId).filter(
    (s) => TERMINAL_SQUAD_STATES.has(s.squad.status),
  );

  return { activeSquads, terminalSquads };
}
